// components/VideoAvatar.js
import { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";

// Contexts & Hooks
import { BrandContext } from "../../App";
import { useAPI } from "../../hooks/useAPI";
import { useUI } from "../../contexts/UIContext";
import { useAuth } from "../../contexts/AuthContext";
import { useWebcam } from "../../hooks/useWebcam";
import { useDraggableVideo } from "../../hooks/useDraggableVideo";

import {
  determineExternalMode,
  determineRedirectPath,
} from "../../utils/navigationUtils";

// Components
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Avatar,
} from "@mui/material";
import { PlayCircleFilledOutlined } from "@mui/icons-material";
import CallToolbar from "../simple/CallToolbar";
import CallScreenSidePanel from "../complex/CallScreenSidePanel";
import LiveCaptions from "../CallScreen/LiveCaptions";
import AudioOnlyAvatar from "../CallScreen/AudioOnlyAvatar";
import ConnectionBanner from "../CallScreen/ConnectionBanner";
import { LiveKitManager } from "../../components/complex/LiveKitManager";
import { VideoContainer } from "./styles.js";
import { useAudioOnlyMode } from "../../hooks/useAudioOnlyMode";
import { useVideoFreezeDetection } from "../../hooks/useVideoFreezeDetection";

// Extracted UI Components
const BoxLoader = ({ message = "Connecting..." }) => (
  <Box
    sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(35, 31, 32, 0.85)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 20,
    }}
  >
    <CircularProgress size={60} sx={{ color: "#ffffff" }} />
    <Typography variant="h5" mt={3} color="white">
      {message}
    </Typography>
  </Box>
);

const VideoAvatar = ({
  config,
  scenario,
  autoConnect = true,
  firstScenario = null,
  simulationType = "training_program",
  startSim = null,
  simError = null,
  feedback_avatar_persona = null,
  forceAudioOnly = false,
  onClose = null,
}) => {
  // Global State & Contexts
  const queryClient = useQueryClient();
  const { brand, trainingProgram } = useContext(BrandContext);
  const { authToken } = useAuth();
  const { theme, setShowCrumbs, isMobile } = useUI();
  const { callAPI } = useAPI();

  // Refs
  const avatarVideoRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const audioRef = useRef(null);
  // Kept in sync so the stable LiveKit effect closure can read the current value
  const personaNameRef = useRef<string | undefined>(
    feedback_avatar_persona?.name ??
      scenario?.avatar_persona?.name ??
      undefined,
  );

  // Constants & Computed
  const isExternalMode = determineExternalMode(
    scenario,
    window.location.pathname,
  );
  const resourcesExist = scenario?.resources?.length >= 1;

  // Custom Hooks
  const { videoEnabled, toggleVideo, setupCamera } = useWebcam(videoRef);
  const { position, isDragging, handleDown } = useDraggableVideo(
    videoRef,
    isMobile,
    simulationType,
  );
  // Local UI State
  const [transcript, setTranscript] = useState([]);
  const [liveKitManager, setLiveKitManager] = useState(null);
  const [connectionState, setConnectionState] = useState({
    connected: false,
    connecting: false,
    error: null,
  });
  const [simulationId, setSimulationId] = useState(null);
  const [isLoading, setIsLoading] = useState(!autoConnect ? false : true);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
  const [hasCompletedSim, setHasCompletedSim] = useState(false);
  const [subTitlesEnabled, setSubTitlesEnabled] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(resourcesExist);
  const [panelValue, setPanelValue] = useState(resourcesExist ? 1 : 0);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(
    firstScenario == null ? true : firstScenario,
  );

  // Audio-only mode state
  const [connectionQuality, setConnectionQuality] = useState("unknown");
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);

  const isVideoFrozen = useVideoFreezeDetection(
    avatarVideoRef,
    connectionState.connected && !!remoteVideoTrack,
  );

  const { isAudioOnly, bannerState, tryVideo } = useAudioOnlyMode({
    firstScenario,
    isConnected: connectionState.connected,
    connectionQuality,
    hasAudioTrack: !!remoteAudioTrack,
    hasVideoTrack: !!remoteVideoTrack,
    isVideoFrozen,
    forceAudioOnly,
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const handleToggleFullscreen = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;

    if (!document.fullscreenElement) {
      element.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    personaNameRef.current =
      feedback_avatar_persona?.name ??
      scenario?.avatar_persona?.name ??
      undefined;
  }, [feedback_avatar_persona, scenario]);

  useEffect(() => {
    if (simulationType !== "feedback_session") {
      setShowCrumbs(false);
      return () => setShowCrumbs(true);
    }
  }, [simulationType, setShowCrumbs]);

  useEffect(() => {
    // Fixed operator precedence bug
    if (simulationType !== "feedback_session") setShowCrumbs(false);

    if (
      Cookies.get("GN-CompletedSim") ||
      window.location.href.includes("externalscenario")
    ) {
      setHasCompletedSim(true);
    }

    const manager = new LiveKitManager(({ track, streams }) => {
      if (track.kind === "video") {
        setRemoteVideoTrack(track);
      }
      if (track.kind === "audio") {
        setRemoteAudioTrack(track);
      }
    });

    manager.setTranscriptHandler((raw) => {
      const entry = JSON.parse(raw);
      if (entry.ai_generated && entry.author?.toLowerCase() === "livekit") {
        entry.author = personaNameRef.current ?? entry.author;
      }
      setTranscript((prev) => [...prev, entry]);
    });

    manager.setConnectionQualityHandler((quality: string) => {
      setConnectionQuality(quality);
    });

    manager.setSpeakingHandler((isUser: boolean, isAvatar: boolean) => {
      setIsUserSpeaking(isUser);
      setIsAvatarSpeaking(isAvatar);
    });

    setLiveKitManager(manager);

    return () => {
      setShowCrumbs(true);
      manager.cleanup();
      document.getElementById("remote-audio")?.remove();
    };
  }, []);

  // useEffect(() => {
  //   if (remoteVideoTrack && avatarVideoRef.current) {
  //     remoteVideoTrack.attach(avatarVideoRef.current);
  //   }
  //   if (remoteAudioTrack && audioRef.current) {
  //     remoteAudioTrack.attach(audioRef.current);
  //   }

  //   // Cleanup dynamically detaches tracks when component unmounts or tracks change
  //   return () => {
  //     remoteVideoTrack?.detach();
  //     remoteAudioTrack?.detach();
  //   };
  // }, [remoteVideoTrack, remoteAudioTrack]);

  useEffect(() => {
    if (remoteVideoTrack && avatarVideoRef.current) {
      remoteVideoTrack.attach(avatarVideoRef.current);
    }

    return () => {
      // Only detach the video track when the video track changes or unmounts
      if (remoteVideoTrack) {
        remoteVideoTrack.detach();
      }
    };
  }, [remoteVideoTrack]);

  useEffect(() => {
    if (remoteAudioTrack && audioRef.current) {
      remoteAudioTrack.attach(audioRef.current);
    }

    return () => {
      // Only detach the audio track when the audio track changes or unmounts
      if (remoteAudioTrack) {
        remoteAudioTrack.detach();
      }
    };
  }, [remoteAudioTrack]);

  // 3. Connection Logic
  const connectToLiveKit = useCallback(async () => {
    if (
      !liveKitManager ||
      connectionState.connecting ||
      (!config.id && !isExternalMode)
    )
      return;

    setConnectionState((prev) => ({ ...prev, connecting: true, error: null }));

    try {
      if (isExternalMode && scenario?.useProvidedToken) {
        setSimulationId(scenario.simulation_id);
        await liveKitManager.connect(scenario.livekit_url, {
          token: scenario.token,
        });
      } else {
        setSimulationId(config.id);
        await liveKitManager.connect(config.livekit_url, {
          token: config.token,
        });
      }

      await liveKitManager.setMicrophoneEnabled(true);
      setConnectionState({ connected: true, connecting: false, error: null });
      setupCamera();
      setIsLoading(false);
    } catch (error) {
      console.error("LiveKit connection error:", error);
      setConnectionState({
        connected: false,
        connecting: false,
        error: error.message || error.detail,
      });
    }
  }, [
    liveKitManager,
    connectionState.connecting,
    config,
    isExternalMode,
    scenario,
    setupCamera,
  ]);

  useEffect(() => {
    if (
      autoConnect &&
      liveKitManager &&
      !connectionState.connected &&
      !connectionState.connecting
    ) {
      connectToLiveKit();
    }
  }, [
    autoConnect,
    liveKitManager,
    connectionState.connected,
    connectionState.connecting,
    connectToLiveKit,
  ]);

  useEffect(() => {
    if (simulationType === "feedback_session" && !simulationId && config?.id) {
      connectToLiveKit();
    }
  }, [config, simulationType, simulationId, connectToLiveKit]);

  // 5. Actions
  const handleEndSimulation = async () => {
    const effectiveSimulationId = simulationId || scenario?.simulationId;
    if (!effectiveSimulationId) return;

    const endpoint = isExternalMode
      ? "rtc_end_external_simulation"
      : "rtc_end_simulation";

    try {
      await callAPI(endpoint, {
        method: "POST",
        token: authToken,
        body: { simulation_id: effectiveSimulationId },
      });

      queryClient.invalidateQueries({ queryKey: ["trainingPrograms"] });
      queryClient.invalidateQueries({ queryKey: ["simulations"] });

      liveKitManager?.cleanup();
      // setSimulationId(null);
      setConnectionState({ connected: false, connecting: false, error: null });

      const redirectUrl = determineRedirectPath({
        scenarioData: scenario || [],
        trainingProgram,
        isExternalMode,
        simulationId: effectiveSimulationId,
        simulationType,
        authToken,
      });

      if (simulationType === "skills_academy_demo") {
        if (onClose) {
          onClose();
          return;
        }
        const programId = config?.program_id;
        window.location.href = programId
          ? `/dashboard/skills_academy/${programId}`
          : "/dashboard/skills_academy";
        return;
      }

      if (simulationType === "on_demand_coach") {
        if (window.location.pathname.includes("external_ondemandcoach")) {
          window.location.href = `/external_ondemandcoach/${effectiveSimulationId}?completed=true&refid=${effectiveSimulationId}`;
        } else {
          window.location.href = "/dashboard/on_demand_coach";
        }
        return;
      }

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        location.reload();
      }
    } catch (error) {
      console.error("Error ending simulation:", error);
    }
  };

  const handlePlaySimulation = async () => {
    try {
      setIsLoading(true);
      await startSim();
      setIsLoading(false);
    } catch (err) {
      simError("Failed to start simulation: " + err.message || err.detail);
    }
  };

  return (
    <VideoContainer
      ref={containerRef}
      id="callscreen-container"
      className={autoConnect ? "full" : "mini"}
      isTheaterMode={isTheaterMode}
      // @ts-ignore — isAudioOnly is a custom prop forwarded to VideoContainer
      isAudioOnly={isAudioOnly}
      sx={{
        ...(isTheaterMode
          ? {
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw!important",
              height: "100dvh!important",
              maxWidth: "none",
              zIndex: 99999, // Sit on top of absolutely everything (navbars, sidebars)
              borderRadius: 0,
              margin: 0,
              aspectRatio: "auto",
              "& #lk-video": {
                zIndex: 999,
              },
            }
          : {
              // --- NORMAL CSS ---
              position: "relative",
              height: CSS.supports("height", "100dvh")
                ? "calc(100dvh - 70px)!important"
                : "calc(100vh - 70px)!important",
              [theme.breakpoints.up("sm")]: {
                height: "auto!important",
              },
              width: "100%",
              maxWidth: "1350px",
              aspectRatio: "16 / 9",
              borderRadius: 2,
              margin: "0 auto",
              left: "0",
            }),

        ...(simulationType === "on_demand_coach" ||
        simulationType === "skills_academy_demo" ||
        isExternalMode
          ? {
              [theme.breakpoints.down("md")]: {
                height: CSS.supports("height", "100dvh")
                  ? "100dvh!important"
                  : "100vh!important",
                top: "0!important",
              },
            }
          : {}),
      }}
    >
      {/* Connection / avatar-unavailable notification banner */}
      <ConnectionBanner state={bannerState} />

      {!isMobile && simulationType !== "feedback_session" && (
        <div className="sim-name-overlay">{scenario?.name}</div>
      )}

      {autoConnect &&
      (isLoading ||
        connectionState.connecting ||
        !connectionState.connected ||
        (isAudioOnly && !remoteAudioTrack)) ? (
        <BoxLoader
          message={
            isAudioOnly && connectionState.connected && !remoteAudioTrack
              ? "Waiting for agent..."
              : "Connecting..."
          }
        />
      ) : (
        <>
          {!autoConnect && !connectionState.connected && (
            <>
              {connectionState.connecting || isLoading ? (
                <BoxLoader
                  message={
                    connectionState.connecting ? "Connecting..." : "Loading..."
                  }
                />
              ) : (
                <button
                  onClick={
                    simulationType === "training_program"
                      ? connectToLiveKit
                      : handlePlaySimulation
                  }
                  id="manual-connect-button"
                >
                  {/* Add preview image if available */}
                  {feedback_avatar_persona?.preview_image && (
                    <img
                      src={feedback_avatar_persona.preview_image}
                      alt=""
                      style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                        zIndex: 0,
                      }}
                    />
                  )}
                  <PlayCircleFilledOutlined
                    style={{ position: "relative", zIndex: 1 }}
                  />
                </button>
              )}
            </>
          )}

          {connectionState.connected && (
            <Paper
              className="video-inner-container"
              elevation={3}
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: "1350px",
                aspectRatio: "16 / 9",
                borderRadius: 2,
                margin: "0 auto",
                left: "0",
                // --- SHARED CSS ---
                backgroundColor: "transparent",
                zIndex: "1200",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                overflow: "hidden",
                filter:
                  !hasCompletedSim &&
                  !isMobile &&
                  simulationType !== "feedback_session"
                    ? "blur(2px)"
                    : "none",
                boxShadow: "none",
                [theme.breakpoints.down("md")]:
                  simulationType !== "feedback_session"
                    ? {
                        height: CSS.supports("height", "100dvh")
                          ? "calc(100dvh - 70px)"
                          : "calc(100vh - 70px)",
                        aspectRatio: "9 / 16",
                        borderRadius: 0,
                        position: "absolute",
                        top: 0,
                        transition: "all 0.2s ease",
                      }
                    : {},
                ...(isExternalMode && simulationType !== "on_demand_coach"
                  ? {
                      [theme.breakpoints.down("md")]: {
                        height: CSS.supports("height", "100dvh")
                          ? "100dvh!important"
                          : "100vh!important",
                        top: "0!important",
                      },
                    }
                  : {}),
                ...(simulationType === "on_demand_coach" ||
                simulationType === "skills_academy_demo"
                  ? window.location.pathname.includes("external")
                    ? {
                        [theme.breakpoints.down("md")]: {
                          height: "auto!important",
                          top: "0!important",
                          position: "absolute",
                          zIndex: "1000",
                        },
                      }
                    : {
                        [theme.breakpoints.down("md")]: {
                          height: CSS.supports("height", "100dvh")
                            ? "100dvh!important"
                            : "100vh!important",
                          top: "0!important",
                          position: "absolute",
                          zIndex: "1000",
                        },
                      }
                  : {}),
              }}
            >
              {/* User webcam PiP — hidden entirely in audio-only mode */}
              {!isAudioOnly && (
                <>
                  <video
                    ref={videoRef}
                    id="video"
                    draggable={false}
                    autoPlay
                    playsInline
                    muted
                    onMouseDown={handleDown}
                    onTouchStart={handleDown}
                    style={{
                      position: "absolute",
                      top: position.y,
                      left: position.x,
                      objectFit: "cover",
                      borderRadius: "8px",
                      cursor: isDragging ? "grabbing" : "grab",
                      backgroundColor: "black",
                      zIndex: 500,
                      display:
                        videoEnabled &&
                        !isLoading &&
                        !connectionState.connecting
                          ? "block"
                          : "none",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                      transition: isDragging ? "none" : "top 0.2s, left 0.2s",
                      transform: "rotateY(180deg)",
                      WebkitTransform: "rotateY(180deg)",
                      MozTransform: "rotateY(180deg)",
                      touchAction: "none",
                    }}
                  />

                  {/* 2. ALWAYS render the Avatar, just hide it if video is enabled */}
                  <Avatar
                    id="avatar-pic"
                    onMouseDown={handleDown}
                    onTouchStart={handleDown}
                    sx={{
                      position: "absolute",
                      top: position.y,
                      left: position.x,
                      width: { xs: "103px", md: "140px", lg: "182px" },
                      height: { xs: "96px", md: "128px", lg: "165px" },
                      cursor: isDragging ? "grabbing" : "grab",
                      borderRadius: "8px",
                      backgroundColor: "black",
                      zIndex: 500,
                      display:
                        !videoEnabled &&
                        !isLoading &&
                        !connectionState.connecting
                          ? "flex"
                          : "none",
                      fontSize: "48px",
                      color: "grey",
                      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
                      touchAction: "none",
                    }}
                  />
                </>
              )}

              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  gap: 15,
                  justifyContent: showSidePanel ? "space-between" : "center",
                  alignItems: "flex-end",
                  zIndex: 30,
                  boxSizing: "border-box",
                  padding: `${theme.customSpacing["2xl"]}`,
                  [theme.breakpoints.down("md")]: {
                    position: "absolute",
                  },
                }}
              >
                <LiveCaptions
                  transcript={transcript}
                  enabled={subTitlesEnabled}
                  isMobile={isMobile}
                />

                <CallToolbar
                  videoEnabled={videoEnabled}
                  handleToggleVideo={toggleVideo}
                  handleEndSimulation={handleEndSimulation}
                  openHelpModal={() => setOnboardingOpen(true)}
                  panelValue={panelValue}
                  setPanelValue={setPanelValue}
                  openSidePanel={() => setShowSidePanel(true)}
                  scenarioData={scenario}
                  isExternalMode={isExternalMode}
                  isMobile={isMobile}
                  isFeedbackSession={simulationType === "feedback_session"}
                  simulationType={simulationType}
                  onHelpTooltipClose={() => setHasCompletedSim(true)}
                  handleToggleFullscreen={() =>
                    setIsTheaterMode(!isTheaterMode)
                  }
                  isFullscreen={isTheaterMode}
                  subTitlesEnabled={subTitlesEnabled}
                  handleToggleSubtitles={() =>
                    setSubTitlesEnabled(!subTitlesEnabled)
                  }
                  isAudioOnly={isAudioOnly}
                  onTryVideo={tryVideo}
                />

                {showSidePanel && (
                  <CallScreenSidePanel
                    scenarioData={scenario}
                    isExternalMode={isExternalMode}
                    tab={panelValue}
                    simulationType={simulationType}
                    showSidePanel={showSidePanel}
                    onClose={() => setShowSidePanel(false)}
                    transcript={transcript}
                    isMobile={isMobile}
                  />
                )}
              </Box>
            </Paper>
          )}
        </>
      )}
      {/* Avatar video feed — hidden in audio-only mode, replaced by AudioOnlyAvatar */}
      <video
        ref={avatarVideoRef}
        id="lk-video"
        autoPlay
        playsInline
        muted
        style={{ display: isAudioOnly ? "none" : "block" }}
      />
      {isAudioOnly && (
        <AudioOnlyAvatar
          isUserSpeaking={isUserSpeaking}
          isAvatarSpeaking={isAvatarSpeaking}
          name={
            feedback_avatar_persona?.name ??
            scenario?.avatar_persona?.name ??
            undefined
          }
          imageUrl={
            feedback_avatar_persona?.preview_image ??
            scenario?.avatar_persona?.preview_image ??
            undefined
          }
        />
      )}
      <audio ref={audioRef} id="lk-audio" autoPlay />
    </VideoContainer>
  );
};

export default VideoAvatar;
